import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { useAppStore } from '../store';

// ── Types ──

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: 'viewer' | 'analyst' | 'admin';
  avatar: string | null;
}

interface LoginResponse {
  token: string;
  user: AuthUser;
}

interface TokenPayload {
  exp: number;
  sub: string;
  role: string;
}

// ── Helpers ──

export function decodeToken(token: string): TokenPayload | null {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = decodeToken(token);
  if (!payload) return true;
  // Consider expired 60s before actual expiry for safety
  return payload.exp * 1000 < Date.now() + 60_000;
}

export function getStoredToken(): string | null {
  return localStorage.getItem('helios_token');
}

function setStoredToken(token: string) {
  localStorage.setItem('helios_token', token);
}

function removeStoredToken() {
  localStorage.removeItem('helios_token');
}

// ── API Calls ──

async function loginApi(email: string, password: string): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>('/auth/login', { email, password });
  return data;
}

async function logoutApi(): Promise<void> {
  await apiClient.post('/auth/logout');
}

async function getCurrentUser(): Promise<AuthUser> {
  const { data } = await apiClient.get<AuthUser>('/auth/me');
  return data;
}

async function refreshTokenApi(): Promise<{ token: string }> {
  const { data } = await apiClient.post<{ token: string }>('/auth/refresh');
  return data;
}

async function forgotPasswordApi(email: string): Promise<void> {
  await apiClient.post('/auth/forgot-password', { email });
}

async function resetPasswordApi(token: string, password: string): Promise<void> {
  await apiClient.post('/auth/reset-password', { token, password });
}

// ── Hooks ──

export function useLogin() {
  const login = useAppStore((s) => s.authLogin);

  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      loginApi(email, password),
    onSuccess: (data) => {
      setStoredToken(data.token);
      login(data.token, data.user);
    },
  });
}

export function useLogout() {
  const logout = useAppStore((s) => s.authLogout);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: logoutApi,
    onSettled: () => {
      removeStoredToken();
      logout();
      queryClient.clear();
    },
  });
}

export function useCurrentUser() {
  const token = getStoredToken();

  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: getCurrentUser,
    enabled: !!token && !isTokenExpired(token),
    staleTime: 5 * 60_000,
    // ⚠️ D.1308 — `retry: false` rendait un echec PASSAGER definitif : le role
    // restait inconnu jusqu'au prochain rechargement, donc les pages admin
    // restaient inaccessibles. On retente les erreurs de transport, mais
    // JAMAIS un 401/403 : ceux-la sont une reponse, pas une panne, et les
    // rejouer ne ferait que marteler le serveur.
    retry: (failureCount: number, error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 2;
    },
  });
}

export function useRefreshToken() {
  const setToken = useAppStore((s) => s.authSetToken);

  return useMutation({
    mutationFn: refreshTokenApi,
    onSuccess: (data) => {
      setStoredToken(data.token);
      // ⚠️ D.1308 — ON NE REECRIT PLUS L'IDENTITE ICI.
      // L'ancien code relisait `user` dans le store et le re-passait a
      // `authLogin`. Son commentaire disait « without changing user » — et il
      // le changeait : quand le store portait encore l'etat initial, le
      // rafraichissement y RECOPIAIT le role par defaut, le figeant juste
      // avant que /auth/me ne reponde. Un rafraichissement de jeton ne
      // renseigne rien sur l'identite : il ne doit donc rien en dire.
      setToken(data.token);
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => forgotPasswordApi(email),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      resetPasswordApi(token, password),
  });
}
